import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/** Surfaces follow agent 1's .glass / .glass-strong / .glass-subtle contract. */
export const glassPanel =
  'glass relative overflow-hidden rounded-[1.75rem] border border-white/55 bg-card/70 text-card-foreground shadow-lg ring-1 ring-inset ring-white/70 backdrop-blur-2xl dark:border-white/10 dark:bg-card/45 dark:ring-white/10';

export const glassStrong =
  'glass-strong rounded-[1.75rem] border border-white/60 bg-background/80 text-card-foreground shadow-xl ring-1 ring-inset ring-white/80 backdrop-blur-2xl dark:border-white/15 dark:bg-background/75 dark:ring-white/10 sm:rounded-[1.75rem]';

export const glassSubtle =
  'glass-subtle rounded-2xl border border-white/45 bg-background/50 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.05]';

export const fieldControl = 'rounded-xl bg-background/60 dark:bg-background/40';

export const pillButton = 'rounded-full';

export function describedBy(id, error, hint) {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

export function apiMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean)
      .join(' ');
    if (text) return text;
  }
  return fallback;
}

export function PageShell({ children, testId, className, ...props }) {
  return (
    <div className={cn('relative max-w-full space-y-6', className)} data-testid={testId} {...props}>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 h-40 w-40 rounded-full bg-sky-400/15 blur-3xl dark:bg-sky-300/10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-16 h-32 w-32 rounded-full bg-emerald-400/10 blur-3xl dark:bg-emerald-300/10"
      />
      <div className="relative space-y-6">{children}</div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">{eyebrow}</p>
        ) : null}
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Field({ id, label, error, hint, required, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-destructive" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </Label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({ id, label, error, hint, required, className, ...props }) {
  return (
    <Field id={id} label={label} error={error} hint={hint} required={required}>
      <Input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        className={cn(fieldControl, error && 'border-destructive focus-visible:ring-destructive', className)}
        {...props}
      />
    </Field>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testId }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-14 text-center" data-testid={testId}>
      <div className={cn(glassSubtle, 'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl')}>
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      </div>
      <p className="font-medium">{title}</p>
      {description ? <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ title = 'Could not load this page', message, onRetry }) {
  return (
    <div role="alert" className={cn(glassPanel, 'flex flex-col items-start gap-3 p-6')}>
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
        <AlertCircle className="h-5 w-5" aria-hidden="true" />
        <h2 className="font-semibold text-foreground">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
      {onRetry ? (
        <Button type="button" variant="outline" className={pillButton} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function LoadingPanels({ count = 2, testId }) {
  return (
    <div className="grid gap-6 xl:grid-cols-2" data-testid={testId} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={cn(glassPanel, 'h-72 animate-pulse bg-muted/40')} />
      ))}
    </div>
  );
}

export function ScrollHint() {
  return <p className="mb-3 text-xs text-muted-foreground sm:hidden">Swipe sideways to see every column.</p>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  destructive = false,
  pending = false,
  onConfirm,
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <AlertDialogContent className={glassStrong}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className={pillButton} disabled={pending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(pillButton, destructive && buttonVariants({ variant: 'destructive' }))}
            disabled={pending}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {pending ? 'Working…' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
